/**
 * Dialog para iniciar nova conversa
 * 
 * Permite ao usuário:
 * - Pesquisar contatos existentes e iniciar conversa
 * - Cadastrar novo contato e depois iniciar conversa
 */

import { useState, useEffect } from 'react';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  Phone, 
  Mail, 
  Loader2, 
  Search, 
  UserPlus, 
  Check, 
  MessageSquare,
  Inbox 
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getStorageUrl } from '@/lib/storage';
import { PhoneInput } from '@/components/ui/phone-input';
import { toast } from 'sonner';
import { normalizeBrazilianPhone, formatPhoneForDisplay, normalizePhoneIdentifier } from '@/lib/phone-utils';
import { usePhoneDuplicateCheck } from '@/hooks/usePhoneDuplicateCheck';

// Interface para contato buscado
interface SearchedContact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
}

// Interface para inbox ativa
interface ActiveInbox {
  id: string;
  name: string;
  identifier: string;
  channel_type: string;
}

interface StartNewConversationDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onOpenChange: (open: boolean) => void;
  /** ID do tenant para buscar contatos e inboxes */
  tenantId: string;
}

export function StartNewConversationDialog({
  open,
  onOpenChange,
  tenantId,
}: StartNewConversationDialogProps) {
  const navigate = useNavigate();
  const { checkPhoneDuplicate } = usePhoneDuplicateCheck();
  
  // Estado da aba ativa
  const [activeTab, setActiveTab] = useState<'search' | 'register'>('search');
  
  // Estados para pesquisa de contatos
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState<SearchedContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<SearchedContact | null>(null);
  
  // Estados do formulário de cadastro
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Estados para seleção de inbox
  const [inboxes, setInboxes] = useState<ActiveInbox[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');
  const [loadingInboxes, setLoadingInboxes] = useState(true);
  
  // Estado de navegação
  const [starting, setStarting] = useState(false);

  // Validação do formulário de cadastro
  const isFormValid = name.trim().length > 0 && phone.replace(/\D/g, '').length >= 10;
  
  // Validação para iniciar conversa
  const canStart = selectedContact && selectedContact.phone && selectedInboxId;

  // Busca inboxes WhatsApp ativas quando o dialog abre
  useEffect(() => {
    if (open && tenantId) {
      fetchActiveInboxes();
    }
  }, [open, tenantId]);

  // Seleciona automaticamente se houver apenas uma inbox
  useEffect(() => {
    if (inboxes.length === 1) {
      setSelectedInboxId(inboxes[0].id);
    }
  }, [inboxes]);

  // Busca contatos do tenant com debounce
  useEffect(() => {
    if (!tenantId || !open) return;
    
    const searchContacts = async () => {
      setLoadingContacts(true);
      try {
        let query = supabase
          .from('contacts')
          .select('id, name, phone, email, avatar_url')
          .eq('tenant_id', tenantId)
          .not('phone', 'is', null)
          .order('name', { ascending: true })
          .limit(50);

        if (searchQuery.trim()) {
          query = query.or(`name.ilike.%${searchQuery}%,phone.ilike.%${searchQuery}%`);
        }

        const { data, error } = await query;
        
        if (error) {
          console.error('[StartNewConversationDialog] Error searching contacts:', error);
          return;
        }
        
        setContacts(data || []);
      } catch (error) {
        console.error('[StartNewConversationDialog] Error:', error);
      } finally {
        setLoadingContacts(false);
      }
    };
    
    const timer = setTimeout(searchContacts, 300);
    return () => clearTimeout(timer);
  }, [tenantId, searchQuery, open]);

  // Limpa estados quando fecha o dialog
  useEffect(() => {
    if (!open) {
      setActiveTab('search');
      setSearchQuery('');
      setSelectedContact(null);
      setName('');
      setPhone('');
      setEmail('');
      setSelectedInboxId(inboxes.length === 1 ? inboxes[0].id : '');
    }
  }, [open, inboxes]);

  const fetchActiveInboxes = async () => {
    setLoadingInboxes(true);
    try {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier, channel_type')
        .eq('tenant_id', tenantId)
        .in('channel_type', ['whatsapp', 'whatsapp_oficial'])
        .eq('status', 'active');

      if (error) throw error;
      setInboxes(data || []);
    } catch (error) {
      console.error('[StartNewConversationDialog] Erro ao buscar inboxes:', error);
      toast.error('Erro ao carregar inboxes');
    } finally {
      setLoadingInboxes(false);
    }
  };

  // Salvar novo contato e selecionar
  const handleSaveContact = async () => {
    if (!isFormValid || saving) return;

    setSaving(true);

    // VALIDAÇÃO: Verificar duplicidade antes de inserir (usando normalização)
    const { isDuplicate, existingContact } = await checkPhoneDuplicate(
      tenantId,
      phone
    );

    if (isDuplicate) {
      toast.error(
        `Este telefone já pertence ao contato "${existingContact?.name || 'Sem nome'}". Use a aba Pesquisar para encontrá-lo.`,
        { duration: 5000 }
      );
      setSaving(false);
      return;
    }

    try {
      // Criar contato com telefone normalizado
      const { data, error } = await supabase
        .from('contacts')
        .insert({
          tenant_id: tenantId,
          name: name.trim(),
          phone: normalizePhoneIdentifier(phone),
          email: email.trim() || null,
        })
        .select('id, name, phone, email, avatar_url')
        .single();

      if (error) throw error;

      // Selecionar o contato criado e voltar para aba de pesquisa
      setSelectedContact(data);
      setActiveTab('search');
      
      // Adicionar à lista de contatos
      setContacts(prev => [data, ...prev]);
      
      // Limpar formulário
      setName('');
      setPhone('');
      setEmail('');
      
      toast.success('Contato criado! Selecione uma inbox para iniciar conversa.');
    } catch (error: any) {
      console.error('[StartNewConversationDialog] Erro ao criar contato:', error);
      toast.error(error.message || 'Erro ao criar contato');
    } finally {
      setSaving(false);
    }
  };

  // Iniciar conversa
  const handleStartConversation = async () => {
    if (!selectedContact || !selectedInboxId) return;

    setStarting(true);
    try {
      // Navega para o chat passando os dados do contato via state
      navigate('/tenant/chat', {
        state: {
          contactId: selectedContact.id,
          contactPhone: selectedContact.phone,
          contactName: selectedContact.name,
          inboxId: selectedInboxId,
        },
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('[StartNewConversationDialog] Erro ao iniciar conversa:', error);
      toast.error('Erro ao iniciar conversa');
    } finally {
      setStarting(false);
    }
  };

  // Extrai iniciais do nome para o avatar
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[500px] max-w-[calc(100vw-2rem)] overflow-hidden"
        onInteractOutside={(e) => (saving || starting) && e.preventDefault()}
        onPointerDownOutside={(e) => (saving || starting) && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Nova Conversa
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'search' | 'register')} className="w-full min-w-0 overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="search" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Pesquisar
            </TabsTrigger>
            <TabsTrigger value="register" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Cadastrar
            </TabsTrigger>
          </TabsList>

          {/* Aba Pesquisar Contatos */}
          <TabsContent value="search" className="space-y-4 mt-4 min-w-0 overflow-hidden">
            {/* Campo de busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou telefone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                disabled={starting}
              />
            </div>

            {/* Lista de contatos */}
            <ScrollArea className="h-[200px] rounded-md border" axis="y">
              {loadingContacts ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <User className="h-8 w-8 mb-2" />
                  <p className="text-sm">
                    {searchQuery ? 'Nenhum contato encontrado' : 'Busque um contato acima'}
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {contacts.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => setSelectedContact(
                        selectedContact?.id === contact.id ? null : contact
                      )}
                      className={`w-full flex items-center gap-3 p-2 rounded-md transition-colors text-left overflow-hidden ${
                        selectedContact?.id === contact.id
                          ? 'bg-primary/10 border border-primary'
                          : 'hover:bg-muted'
                      }`}
                      disabled={starting}
                    >
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage 
                          src={contact.avatar_url ? getStorageUrl(contact.avatar_url) : undefined} 
                        />
                        <AvatarFallback className={getAvatarColorClasses(contact.name)}>
                          {getInitials(contact.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {contact.name || 'Sem nome'}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {formatPhoneForDisplay(contact.phone)}
                        </p>
                      </div>
                      {selectedContact?.id === contact.id && (
                        <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Contato selecionado */}
            {selectedContact && (
              <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-md border border-primary/20 min-w-0 overflow-hidden">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="text-sm truncate min-w-0">
                  Selecionado: <strong className="truncate">{selectedContact.name || selectedContact.phone}</strong>
                </span>
              </div>
            )}

            {/* Seleção de inbox */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                Inbox WhatsApp
              </Label>

              {loadingInboxes ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : inboxes.length === 0 ? (
                <div className="text-center py-3 text-muted-foreground bg-muted/50 rounded-md">
                  <p className="text-sm">Nenhuma inbox WhatsApp ativa encontrada.</p>
                </div>
              ) : (
                <Select
                  value={selectedInboxId}
                  onValueChange={setSelectedInboxId}
                >
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="Selecione uma inbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {inboxes.map((inbox) => (
                      <SelectItem key={inbox.id} value={inbox.id}>
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{inbox.name}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {inbox.identifier}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </TabsContent>

          {/* Aba Cadastrar Novo Contato */}
          <TabsContent value="register" className="space-y-4 mt-4 min-w-0 overflow-hidden">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="new-contact-name" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Nome *
              </Label>
              <Input
                id="new-contact-name"
                placeholder="Ex: João Silva"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Telefone */}
            <div className="space-y-2">
              <Label htmlFor="new-contact-phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Telefone *
              </Label>
              <PhoneInput
                id="new-contact-phone"
                value={phone}
                onChange={setPhone}
                placeholder="11 99999-9999"
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Digite DDD + número (ex: 11 99999-9999)
              </p>
            </div>

            {/* Email (opcional) */}
            <div className="space-y-2">
              <Label htmlFor="new-contact-email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email (opcional)
              </Label>
              <Input
                id="new-contact-email"
                type="email"
                placeholder="email@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={saving}
              />
            </div>

            <Button
              onClick={handleSaveContact}
              disabled={!isFormValid || saving}
              className="w-full"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Salvar e Continuar
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving || starting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleStartConversation}
            disabled={!canStart || starting || saving}
          >
            {starting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Iniciando...
              </>
            ) : (
              <>
                <MessageSquare className="h-4 w-4 mr-2" />
                Iniciar Conversa
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
